import { useState, useEffect, useCallback } from "react";
import { brandService } from "../../../shared/services/apis";
import type {
  Brand,
  BrandQueryParams,
  CreateBrandData,
  UpdateBrandData,
} from "../../../shared/types";

interface UseBrandManagementReturn {
  brands: Brand[];
  loading: boolean;
  error: string | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  fetchBrands: (params?: BrandQueryParams) => Promise<void>;
  createBrand: (data: CreateBrandData) => Promise<void>;
  updateBrand: (id: string, data: UpdateBrandData) => Promise<void>;
  activateBrand: (id: string) => Promise<void>;
  deactivateBrand: (id: string) => Promise<void>;
  deleteBrand: (id: string) => Promise<void>;
  refreshBrands: () => Promise<void>;
}

export const useBrandManagement = (): UseBrandManagementReturn => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchBrands = useCallback(async (params?: BrandQueryParams) => {
    try {
      setLoading(true);
      setError(null);
      await brandService.getBrands(params).then((data) => setBrands(data));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch brands";
      setError(errorMessage);
      console.error("Error fetching brands:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createBrand = useCallback(async (data: CreateBrandData) => {
    try {
      setCreating(true);
      setError(null);
      const newBrand = await brandService.createBrand(data);
      setBrands((prev) => [newBrand, ...prev]);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create brand";
      setError(errorMessage);
      throw err; // Re-throw to handle in component
    } finally {
      setCreating(false);
    }
  }, []);

  const updateBrand = useCallback(async (id: string, data: UpdateBrandData) => {
    try {
      setUpdating(true);
      setError(null);
      const updatedBrand = await brandService.updateBrand(id, data);
      setBrands((prev) =>
        prev.map((brand) => (brand.id === id ? updatedBrand : brand))
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update brand";
      setError(errorMessage);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  const activateBrand = useCallback(async (id: string) => {
    try {
      setUpdating(true);
      setError(null);
      const updatedBrand = await brandService.activateBrand(id);
      setBrands((prev) =>
        prev.map((brand) => (brand.id === id ? updatedBrand : brand))
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to activate brand";
      setError(errorMessage);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  const deactivateBrand = useCallback(async (id: string) => {
    try {
      setUpdating(true);
      setError(null);
      const updatedBrand = await brandService.deactivateBrand(id);
      setBrands((prev) =>
        prev.map((brand) => (brand.id === id ? updatedBrand : brand))
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to deactivate brand";
      setError(errorMessage);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  const deleteBrand = useCallback(async (id: string) => {
    try {
      setDeleting(true);
      setError(null);
      await brandService.deleteBrand(id);
      setBrands((prev) => prev.filter((brand) => brand.id !== id));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete brand";
      setError(errorMessage);
      throw err;
    } finally {
      setDeleting(false);
    }
  }, []);

  const refreshBrands = useCallback(async () => {
    await fetchBrands();
  }, [fetchBrands]);

  // Initial fetch
  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  return {
    brands,
    loading,
    error,
    creating,
    updating,
    deleting,
    fetchBrands,
    createBrand,
    updateBrand,
    activateBrand,
    deactivateBrand,
    deleteBrand,
    refreshBrands,
  };
};
